import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  doc,
  onSnapshot,
  type DocumentSnapshot,
  type FirestoreError,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "@/Utils/firebase";
import { useAuth } from "@/context/auth.context";
import { logEvent, LogType } from "@/Utils/logger";
import { Events } from "@/telemetry/events";
import {
  reconcileOrphanedScanState,
  watchOrphanedScanState,
} from "@/Utils/scanRecovery";
import {
  fetchScansList,
  isFinishedStatus,
  isJustSubmitted,
  readInFlightSubmission,
  scansListKey,
  upsertScanInList,
  SCANS_LIST_BUDGET_MS,
  SCANS_LIST_STALE_MS,
  type InFlightSubmission,
  type ScanListDoc,
} from "@/Utils/scansList";

export type ScansListScreen = "AvatarScreen" | "AvatarSelect";

export interface ScansList {
  /**
   * What the list shows, the synthetic "Submitting Scans ..." doc included.
   * `undefined` means NOT KNOWN YET, never "no avatars": render a spinner in
   * the list area, not the empty state.
   */
  docs: ScanListDoc[] | undefined;
  /**
   * Cover the screen: a first visit with nothing cached, until the server
   * answers, the load fails, or `SCANS_LIST_BUDGET_MS` passes (BUG-0014).
   */
  showSpinner: boolean;
  /**
   * This device's submission has no scan doc yet. `undefined` until the
   * mount-time orphan check has settled, so a dead leftover never shows.
   */
  submissionInFlight: boolean | undefined;
}

/** The card for a submission whose scan doc does not exist yet. */
const submittingDoc = (
  inFlight: InFlightSubmission,
  uid: string | undefined,
): ScanListDoc => ({
  id: inFlight.scanId,
  name: inFlight.name,
  userId: uid,
  timestamp: inFlight.timestamp,
  pipelineStatus: "UPLOADING",
  storagePath: "xyz",
});

interface Visit {
  startedAt: number;
  settled: boolean;
  timedOut: boolean;
}

/**
 * The avatars list for AvatarScreen (`/`) and AvatarSelect (`/send-avatar`).
 *
 * - The list is read once and kept in the react-query cache for the life of
 *   the page, shared by both screens. A visit inside `SCANS_LIST_STALE_MS`
 *   makes no server call; after it, the cached list still shows at once and
 *   one background refetch brings it up to date.
 * - Scans still being processed (`isJustSubmitted`), and this device's
 *   submission before its scan doc exists, are each watched with one doc
 *   listener while the screen is open. A listener stops when its scan is
 *   approved or rejected, or when the screen closes. Its updates go into the
 *   cache, so the next visit starts from them.
 * - One set of `list.scans.*` rows per visit (docs/telemetry/EVENTS.md).
 */
export const useScansList = (screen: ScansListScreen): ScansList => {
  const { user } = useAuth();
  const uid = user?.uid;
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: scansListKey(uid ?? ""),
    queryFn: () => fetchScansList(uid as string),
    enabled: !!uid,
    staleTime: SCANS_LIST_STALE_MS,
    gcTime: Infinity,
  });

  // ---- The visit: its deadline and its rows ------------------------------
  const visit = useRef<Visit | null>(null);
  const [released, setReleased] = useState(false);

  useEffect(() => {
    if (!uid) return;
    setReleased(false);
    const cached = queryClient.getQueryData<ScanListDoc[]>(scansListKey(uid));
    const current: Visit = {
      startedAt: Date.now(),
      settled: cached !== undefined,
      timedOut: false,
    };
    visit.current = current;
    if (cached !== undefined) {
      void logEvent(Events.LIST_SCANS_RESOLVED, "[telemetry] scans list resolved", {
        data: {
          screen,
          source: "memory",
          waitedMs: 0,
          docCount: cached.length,
          afterTimeout: false,
        },
      });
      return;
    }
    const deadline = setTimeout(() => {
      if (current.settled) return;
      current.timedOut = true;
      setReleased(true);
      void logEvent(
        Events.LIST_SCANS_TIMEOUT,
        "[telemetry] scans list timed out, releasing the spinner",
        {
          level: LogType.WARNING,
          data: {
            screen,
            waitedMs: Date.now() - current.startedAt,
            budgetMs: SCANS_LIST_BUDGET_MS,
          },
        },
      );
    }, SCANS_LIST_BUDGET_MS);
    return () => {
      // A visit the user ended writes nothing more.
      current.settled = true;
      clearTimeout(deadline);
    };
  }, [uid, screen, queryClient]);

  const failed = list.status === "error" && list.fetchStatus === "idle";
  useEffect(() => {
    const current = visit.current;
    if (!current || current.settled) return;
    if (list.data !== undefined) {
      current.settled = true;
      void logEvent(Events.LIST_SCANS_RESOLVED, "[telemetry] scans list resolved", {
        data: {
          screen,
          source: "server",
          waitedMs: Date.now() - current.startedAt,
          docCount: list.data.length,
          afterTimeout: current.timedOut,
        },
      });
    } else if (failed) {
      current.settled = true;
      setReleased(true);
      const error = list.error as Partial<FirestoreError> | null;
      void logEvent(Events.LIST_SCANS_ERROR, "[telemetry] scans list load failed", {
        level: LogType.ERROR,
        data: {
          screen,
          waitedMs: Date.now() - current.startedAt,
          code: error?.code ?? null,
          message: error?.message ?? null,
          afterTimeout: current.timedOut,
        },
      });
    }
  }, [list.data, failed, list.error, screen]);

  // ---- This device's submission, from localStorage -----------------------
  // `undefined` until the mount-time orphan check settles: showing the card
  // before it would flash a dead leftover's "Submitting Scans ..." card.
  const [inFlight, setInFlight] = useState<InFlightSubmission | null | undefined>(
    undefined,
  );
  useEffect(() => {
    let active = true;
    const reread = () => {
      if (active) setInFlight(readInFlightSubmission());
    };
    void reconcileOrphanedScanState(screen).then(reread);
    // One look on mount is not enough: the heartbeat proving the owning page
    // died goes stale seconds later (HERE_BE_DRAGONS.md, scanRecovery).
    const stopWatching = watchOrphanedScanState(`${screen}:watch`, reread);
    const onVisibility = () => {
      if (document.visibilityState === "hidden") return;
      void reconcileOrphanedScanState(`${screen}:visibility`).then(reread);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      active = false;
      stopWatching();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [screen]);

  // ---- Scans still being processed ---------------------------------------
  // The latest snapshot of each watched scan, this visit. It wins over the
  // cached copy: it comes from a live listener, and a refetch that started
  // before the scan finished must not put it back to "processing".
  const [seen, setSeen] = useState<Record<string, ScanListDoc>>({});
  const watching = useRef(new Map<string, Unsubscribe>());
  const done = useRef(new Set<string>());

  const known = useMemo(() => {
    if (list.data === undefined) return undefined;
    const merged = list.data.map((cached) => seen[cached.id] ?? cached);
    Object.values(seen).forEach((watched) => {
      if (!list.data.some((cached) => cached.id === watched.id)) merged.push(watched);
    });
    return merged;
  }, [list.data, seen]);

  const submittingWithoutDoc =
    inFlight === undefined
      ? undefined
      : inFlight !== null &&
        !(known ?? []).some((scan) => scan.id === inFlight.scanId);

  // For the watch error row: was the failed watch on a scan the list knew,
  // or on a submission whose doc never appeared?
  const knownIds = useRef(new Set<string>());
  useEffect(() => {
    knownIds.current = new Set((known ?? []).map((scan) => scan.id));
  }, [known]);

  const docs = useMemo(() => {
    if (known === undefined) return undefined;
    return submittingWithoutDoc && inFlight
      ? [...known, submittingDoc(inFlight, uid)]
      : known;
  }, [known, submittingWithoutDoc, inFlight, uid]);

  const nowSeconds = Math.floor(Date.now() / 1000);
  const wanted = new Set<string>();
  (known ?? []).forEach((scan) => {
    if (isJustSubmitted(scan, nowSeconds)) wanted.add(scan.id);
  });
  if (inFlight) wanted.add(inFlight.scanId);
  const wantedKey = Array.from(wanted).sort().join(",");

  useEffect(() => {
    if (!uid) return;
    const want = new Set(wantedKey ? wantedKey.split(",") : []);
    watching.current.forEach((stop, id) => {
      if (!want.has(id)) {
        stop();
        watching.current.delete(id);
      }
    });
    want.forEach((id) => {
      if (watching.current.has(id) || done.current.has(id)) return;
      let docExisted = false;
      const finish = () => {
        done.current.add(id);
        watching.current.get(id)?.();
        watching.current.delete(id);
      };
      const stop = onSnapshot(
        doc(db, "scans", id),
        (snapshot: DocumentSnapshot) => {
          if (!snapshot.exists()) return;
          docExisted = true;
          const scan: ScanListDoc = { id: snapshot.id, ...snapshot.data() };
          upsertScanInList(queryClient, uid, scan);
          setSeen((previous) => ({ ...previous, [id]: scan }));
          if (isFinishedStatus(scan.pipelineStatus)) finish();
        },
        (error: FirestoreError) => {
          void logEvent(
            Events.LIST_SCAN_WATCH_ERROR,
            "[telemetry] a just-submitted scan's listener failed",
            {
              level: LogType.ERROR,
              data: {
                screen,
                code: error?.code ?? null,
                message: error?.message ?? null,
                docExisted: docExisted || knownIds.current.has(id),
              },
            },
          );
          finish();
        },
      );
      watching.current.set(id, stop);
    });
  }, [uid, wantedKey, queryClient, screen]);

  useEffect(() => {
    const running = watching.current;
    const finished = done.current;
    return () => {
      running.forEach((stop) => stop());
      running.clear();
      finished.clear();
      setSeen({});
    };
  }, [uid]);

  return {
    docs,
    showSpinner: list.data === undefined && !released,
    submissionInFlight: submittingWithoutDoc,
  };
};
